import { createAsset } from '$/api/canvasRquest';
import { getUrlSearchParams } from '$/utils';
import { AssetToolType, TaskCategory } from '$/api/server.types';

/** TaskCategory → AssetToolType 映射：将前端任务类别转换为后端 webapi 约定的 tool_type 枚举值 */
const TASK_CATEGORY_TO_ASSET_TOOL_TYPE: Partial<Record<TaskCategory, AssetToolType>> = {
  [TaskCategory.PNG2SVG]: AssetToolType.PNG2SVG,
  [TaskCategory.FUSION]: AssetToolType.IMAGE_FUSION,
  [TaskCategory.CUTOUT]: AssetToolType.IMAGE_CUTOUT,
  [TaskCategory.MAGICSR]: AssetToolType.IMAGE_UPSCALER,
  [TaskCategory.MODIFY_TEXT]: AssetToolType.TEXT_EDITING,
  [TaskCategory.INPAINT]: AssetToolType.IMAGE_INPAINT,
  [TaskCategory.KONTEXT_INPAINT]: AssetToolType.IMAGE_INPAINT,
  [TaskCategory.ERASER]: AssetToolType.IMAGE_ERASER,
  [TaskCategory.EXTEND]: AssetToolType.IMAGE_EXTENSION,
  [TaskCategory.LAYERING]: AssetToolType.IMAGE_LAYERING,
};

/** 将 TaskCategory 转换为后端资产库 tool_type，无映射时原值回退 */
export function resolveAssetToolType(taskCategory: TaskCategory | string): string {
  return TASK_CATEGORY_TO_ASSET_TOOL_TYPE[taskCategory as TaskCategory] ?? taskCategory;
}

/** 登记画布工具产出图片时的可选维度与展示字段 */
export interface RegisterCanvasToolImageOptions {
  /** 成品图 CDN 地址（入库主 url） */
  url: string;
  /** 写入资产库 `tool_type`，与后端 webapi 任务回调枚举对应 */
  toolType: string;
  /** 可选任务 id，写入 `task_id` */
  taskId?: string;
  /** 文件扩展名（不含点），默认 png */
  ext?: string;
  /** 展示用文件名 */
  name?: string;
  /** 像素宽 */
  width?: number;
  /** 像素高 */
  height?: number;
  /** 缩略图 / 水印预览地址 */
  thumbnailUrl?: string;
  /** 有水印成品地址时写入 createAsset.watermark_url */
  watermarkUrl?: string;
}

/**
 * 将画布内置工具产出的图片登记到资产库：`task_type` 固定为 `tool`，来源维度写入 `tool_type`。
 *
 * @param options 入库参数
 * @returns 后端 `asset_id`；失败时返回 undefined（仅 console.warn，不抛错）
 */
export async function registerCanvasToolImageToAssetLibrary(
  options: RegisterCanvasToolImageOptions,
): Promise<number | undefined> {
  const url = (options.url || '').trim();
  if (!url) return undefined;
  const roomId = getUrlSearchParams().get('room_id')?.trim();
  const extRaw = (options.ext || 'png').replace(/^\./, '').toLowerCase();
  try {
    const { asset_id } = await createAsset({
      task_type: 'tool',
      tool_type: options.toolType,
      material_type: 'image',
      url,
      ...(options.taskId ? { task_id: options.taskId } : {}),
      ...(extRaw ? { ext: extRaw } : {}),
      ...(options.name?.trim() ? { name: options.name.trim() } : {}),
      ...(options.width != null && options.width > 0 ? { width: Math.round(options.width) } : {}),
      ...(options.height != null && options.height > 0 ? { height: Math.round(options.height) } : {}),
      ...(options.thumbnailUrl?.trim() ? { thumbnail_url: options.thumbnailUrl.trim() } : {}),
      ...(options.watermarkUrl?.trim() ? { watermark_url: options.watermarkUrl.trim() } : {}),
      ...(roomId ? { room_id: roomId } : {}),
    });
    return asset_id;
  } catch (error) {
    console.warn('[registerCanvasToolImageToAssetLibrary] createAsset failed', url, options.toolType, error);
    return undefined;
  }
}
